import * as focusTrap from 'focus-trap';
import { hide as hideOverlay, show as showOverlay, create as createOverlay } from 'utils/overlay';
import { transitionEnd, repaint } from 'utils/node';

type Events = 'open' | 'opened' | 'close' | 'closed';

export default class {
  private isActive: boolean;

  private readonly container: HTMLElement;

  private focusTrap;

  protected classes: { active: string };

  private overlay: HTMLElement | boolean;

  constructor(element: string | HTMLElement, overlay = false) {
    this.container = typeof element === 'string'
      ? document.querySelector(element)
      : element;

    if (!(this.container instanceof HTMLElement)) {
      throw new TypeError(typeof element === 'string'
        ? `Could not find element matching selector: ${element}`
        : `HTMLElement was not passed to constructor. Instead ${element} was passed`);
    }

    this.overlay = overlay;
    this.isActive = false;
    this.container.hidden = true;

    this.focusTrap = focusTrap.createFocusTrap(this.container, {
      allowOutsideClick: true,
    });

    if (this.overlay) {
      createOverlay(this.overlay);
    }

    this.clickEvent = this.clickEvent.bind(this);
    this.keydownEvent = this.keydownEvent.bind(this);
    this.addEventListeners = this.addEventListeners.bind(this);
  }

  private addEventListeners(): void {
    document.addEventListener('touchstart', this.clickEvent);
    document.addEventListener('click', this.clickEvent);
    document.addEventListener('keydown', this.keydownEvent);
    this.focusTrap.activate();
  }

  private removeEventListeners(): void {
    document.removeEventListener('touchstart', this.clickEvent);
    document.removeEventListener('click', this.clickEvent);
    document.removeEventListener('keydown', this.keydownEvent);
    this.focusTrap.deactivate();
  }

  private clickEvent(event: MouseEvent | TouchEvent): void {
    if (!(event.target instanceof Node)) {
      return;
    }

    if (!this.container.contains(event.target)) {
      event.stopPropagation();

      this.close();
    }
  }

  private keydownEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  toggle(): void {
    if (this.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.isActive) {
      return;
    }

    this.dispatchEvent('open');

    this.isActive = true;
    this.container.hidden = false;
    repaint(this.container);

    this.container.classList.add(this.classes.active);
    showOverlay(this.overlay);

    setTimeout(() => {
      this.addEventListeners();
    }, 0);

    this.dispatchEvent('opened');
  }

  close(): void {
    if (this.isActive === false) {
      return;
    }

    this.dispatchEvent('close');

    this.isActive = false;
    this.container.classList.remove(this.classes.active);
    hideOverlay(this.overlay);

    this.removeEventListeners();

    transitionEnd(this.container, () => {
      if (this.isActive === false) {
        this.container.hidden = true;
      }
    }, { once: true });

    this.dispatchEvent('closed');
  }

  private dispatchEvent(type: Events): void {
    const event = new Event(type);

    this.container.dispatchEvent(event);
  }

  on(type: Events, callback: CallableFunction): void {
    this.container.addEventListener(type, (event) => callback(event));
  }
}
